import React, {useState} from 'react';
import {Button, ButtonGroup, Dropdown, DropdownProps, InputGroup, Navbar, Form} from 'react-bootstrap';
import {BsBoxSeam, BsChevronLeft, BsChevronRight, BsGearFill, BsHammer, BsList, BsMoon, BsSun, BsWrench} from "react-icons/bs";
import {TbLayoutSidebarLeftExpand, TbLayoutSidebarRightExpand} from "react-icons/tb";
import {RiLogoutBoxLine} from "react-icons/ri";
import {Link} from "react-router-dom";
import {useSearchEvents} from "./SearchEvents";
import {MdCancel} from "react-icons/md";
import {DEPARTMENTS, Department, IProductionZone} from "../data/interfaces";
import {DEPARTMENT_ROUTE_PATH_BY_DEPARTMENT} from "../utils/departmentUtils";

const SEARCH_ROUTES = Object.entries(DEPARTMENT_ROUTE_PATH_BY_DEPARTMENT)
  .map(([prefix, path]) => ({prefix, path}));

interface HeaderProps {
  toggleSidebar: () => void;
  isCollapsed: boolean;
  handlePrev: () => void;
  handleToday: () => void;
  handleNext: () => void;
  jumpToDate: string;
  setJumpToDate: (date: string) => void;
  handleJumpToDate: () => void;
  handleNewEventClick: () => void;
  unscheduledTravelersCount: number;
  formattedDate: string;
  currentDepartment: Department;
  productionZones?: IProductionZone[];
  selectedProductionZone?: IProductionZone | null;
  setSelectedProductionZoneId?: (id: string) => void;
  theme: string;
  toggleTheme: () => void;
  signOut: () => void;
}

const Header: React.FC<HeaderProps> = ({
                                         toggleSidebar,
                                         isCollapsed,
                                         handlePrev,
                                         handleToday,
                                         handleNext,
                                         jumpToDate,
                                         setJumpToDate,
                                         handleJumpToDate,
                                         handleNewEventClick,
                                         unscheduledTravelersCount,
                                         formattedDate,
                                         currentDepartment,
                                         productionZones = [],
                                         selectedProductionZone,
                                         setSelectedProductionZoneId,
                                         theme,
                                         toggleTheme,
                                         signOut
                                       }) => {
  //state variables
  const [searchQuery, setSearchQuery] = useState('');
  const suggestions = useSearchEvents(searchQuery);
  const shouldShowProductionZoneSelect =
    currentDepartment === DEPARTMENTS.production
    && productionZones.length > 0
    && !!selectedProductionZone
    && !!setSelectedProductionZoneId;

  /**
   * Custom toggle component for the dropdown
   */
  const CustomToggle = React.forwardRef<HTMLButtonElement, DropdownProps>(({children, onClick}, ref) => (
    <button
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick?.(e);
      }}
      className="btn btn-link calendar-menu-toggle"
    >
      {children}
    </button>
  ));

  const renderSuggestions = () => {
    if (searchQuery && suggestions.length === 0) {
      return (
        <ul
          className="dropdown-menu show p-2 border border-secondary text-black position-absolute top-100 end-0 overflow-y-auto max-vh-70 width-500px calendar-search-results"
        >
          <li className="dropdown-item px-2 py-1 border-bottom border-secondary text-truncate calendar-search-result">
            No results
          </li>
        </ul>
      );
    }

    if (suggestions.length === 0) {
      return null;
    }

    return (
      <ul
        className="dropdown-menu show p-2 border border-secondary text-black position-absolute top-100 end-0 overflow-y-auto max-vh-70 width-500px calendar-search-results"
      >
        {suggestions.map((hit, index) => {
          const { searchIndexData, formattedDate } = hit;
          const { title, subtitle, dateRange } = searchIndexData || {};

          // Access highlighted values
          const highlightedTitle = hit._highlightResult?.searchIndexData?.title?.value ?? title ?? '';
          const highlightedSubtitle = hit._highlightResult?.searchIndexData?.subtitle?.value ?? subtitle ?? '';
          const highlightedDateRange = hit._highlightResult?.searchIndexData?.dateRange?.value ?? dateRange ?? '';

          const encodedDate = encodeURIComponent(formattedDate);
          const matchedRoute = SEARCH_ROUTES.find(({prefix}) => subtitle?.startsWith(prefix));
          const linkPath = matchedRoute ? `/${matchedRoute.path}/${encodedDate}` : '#';

          return (
            <li
              key={index}
              className="dropdown-item px-2 py-1 border-bottom border-secondary text-truncate calendar-search-result"
            >
              {title ? (
                <Link
                  to={linkPath}
                  className="text-decoration-none text-black d-block text-truncate calendar-search-result-link"
                >
                <span
                  className="fw-bold text-truncate"
                  dangerouslySetInnerHTML={{ __html: highlightedTitle }}
                />
                  <div className="d-flex justify-content-between align-items-center">
                  <span
                    className="text-truncate"
                    dangerouslySetInnerHTML={{ __html: highlightedSubtitle }}
                  />
                    <span
                      className="text-muted text-truncate"
                      dangerouslySetInnerHTML={{__html: highlightedDateRange}}
                    />
                  </div>
                </Link>
              ) : (
                'No title available'
              )}
            </li>
          );
        })}
      </ul>
    );
  };
  return (
    <Navbar className={`navbar onsite-calendar-header ${theme !== 'dark' ? 'bg-white' : ''} ${isCollapsed ? 'ms-3' : 'ms-4'} transition`}>
      <div className="d-flex align-items-center">
        <Button
          onClick={toggleSidebar}
          type="button"
          variant="primary"
          className="d-none d-md-inline-flex me-2 align-items-center"
        >
          {isCollapsed ? <TbLayoutSidebarLeftExpand size={22}/> : <TbLayoutSidebarRightExpand size={22}/>}
          <span className="badge bg-danger ms-2 p-1">{unscheduledTravelersCount}</span>
        </Button>
        <ButtonGroup className="me-2">
          <Button variant="secondary" onClick={handlePrev}>
            <BsChevronLeft/>
          </Button>
          <Button variant="secondary" onClick={handleToday} className="px-3">
            Today
          </Button>
          <Button variant="secondary" onClick={handleNext}>
            <BsChevronRight/>
          </Button>
        </ButtonGroup>

        {/* Date Input and Go Button */}
        <InputGroup className="me-2 d-none d-md-inline-flex">
          <Form.Control
            type="date"
            value={jumpToDate}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setJumpToDate(e.target.value)}
            className="border-secondary rounded-end-0 calendar-date-input"
          />
          <Button variant="secondary" onClick={handleJumpToDate}>
            Go
          </Button>
        </InputGroup>

        {shouldShowProductionZoneSelect && (
          <Form.Select
            aria-label={`${DEPARTMENTS.production} zone`}
            className="border border-secondary calendar-production-zone-select me-2"
            value={selectedProductionZone.id}
            onChange={(event) => {
              setSelectedProductionZoneId(event.target.value);
              event.currentTarget.blur();
            }}
          >
            {productionZones.map((productionZone) => (
              <option key={productionZone.id} value={productionZone.id}>
                {productionZone.name}
              </option>
            ))}
          </Form.Select>
        )}

        <Button variant="success" onClick={handleNewEventClick} className="d-none d-md-inline-flex text-nowrap">
          New Event
        </Button>
      </div>
      <div className="d-flex align-items-center ms-auto">
        {/*SearchBar div*/}
        <div id="searchbox" className="d-flex flex-column position-relative me-3 w-100">
          <div className="d-flex">
            <input
              type="text"
              placeholder="Search events"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-control border border-secondary calendar-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                className="btn position-absolute top-50 end-0 translate-middle-y pe-2 calendar-search-clear"
                onClick={() => setSearchQuery('')} // Clear the search input
                aria-label="Clear search"
              >
                <MdCancel/> {/* Use the MdCancel icon */}
              </button>
            )}
          </div>
          {renderSuggestions()}
        </div>
        {/*Dropdown div*/}
        <div className="d-flex align-items-center">
          <Dropdown align="end" drop="down">
            <Dropdown.Toggle as={CustomToggle} id="dropdown-basic">
              <BsList size={30}/>
            </Dropdown.Toggle>
            <Dropdown.Menu className="calendar-dropdown-menu">
              {theme === 'light' && (
                <Dropdown.Item onClick={toggleTheme}>
                  <BsMoon/> Use Dark Mode
                </Dropdown.Item>
              )}
              {theme === 'dark' && (
                <Dropdown.Item onClick={toggleTheme}>
                  <BsSun/> Use Light Mode
                </Dropdown.Item>
              )}
              <Dropdown.Item as={Link} to={`/${DEPARTMENT_ROUTE_PATH_BY_DEPARTMENT[DEPARTMENTS.installation]}/${formattedDate}`}>
                <BsHammer/> {DEPARTMENTS.installation}
              </Dropdown.Item>
              <Dropdown.Item as={Link} to={`/${DEPARTMENT_ROUTE_PATH_BY_DEPARTMENT[DEPARTMENTS.customerService]}/${formattedDate}`}>
                <BsWrench/> {DEPARTMENTS.customerService}
              </Dropdown.Item>
              <Dropdown.Item as={Link} to={`/${DEPARTMENT_ROUTE_PATH_BY_DEPARTMENT[DEPARTMENTS.assembly]}/${formattedDate}`}>
                <BsGearFill/> {DEPARTMENTS.assembly}
              </Dropdown.Item>
              <Dropdown.Item as={Link} to={`/${DEPARTMENT_ROUTE_PATH_BY_DEPARTMENT[DEPARTMENTS.production]}/${formattedDate}`}>
                <BsBoxSeam/> {DEPARTMENTS.production}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => signOut()}>
                <RiLogoutBoxLine/> Sign out
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </Navbar>
  );
};

export default Header;
